import { IGridNativeStateValues, IXrmGridRelationship, XrmGridType, XrmRelationshipType } from "./interfaces";
import { IQuickFindView, IViewDefinition } from "../native/View/interfaces/viewdefinition";
import { EntityDefinition as IEntityDefinition } from '@src/app/interfaces/entitydefinition';
import { Attribute, Constants, DataProvider, Dataset, DataTypes, FetchXml, FetchXmlBuilder, FetchXmlDataProvider, IAggregationMetadata, IColumn, ICommand, IFetchXmlDataProviderColumn, ILayout, IRetrieveRecordCommandOptions, MemoryDataProvider, Operators, PromiseCache, Sanitizer, Type } from "@talxis/client-libraries";
import { EntityDefinition } from "@src/app/classes/definitions/EntityDefinition";
import { ViewDefinition } from "@src/app/classes/definitions/ViewDefinition";
import { DomParser } from "@src/app/Constants";
import { sanitizeGuid } from "@src/app/Functions";
import { TransactionCurrencyDefinition } from "@src/app/classes/definitions/TransactionCurrencyDefinition";
import { HistoryManager } from "@src/providers/HistoryProvider/HistoryManager";
import { DatasetDependencies } from "./DatasetDependencies";
import { ViewSelector } from "./ViewSelector/ViewSelector";
import { DatasetRibbon } from "@src/app/classes/models/Ribbon/DatasetRibbon";
import { QuickFind } from "./QuickFind/QuickFind";
import { GridContext } from "./GridContext";
import { State } from "@src/providers/HistoryProvider/State";

interface IState {
    values: React.MutableRefObject<IGridNativeStateValues>;
    setDefaultValues: (state: IGridNativeStateValues) => void;
}

const layoutCache = new PromiseCache<IColumn[]>();
const titleCache = new PromiseCache<string>();
const quickFindCache = new PromiseCache<string>();

class PortalFetchXmlDataProvider extends FetchXmlDataProvider {
    public ribbon: DatasetRibbon;
    public async onRetrieveRecordCommand(defaultAction: (options?: IRetrieveRecordCommandOptions) => Promise<ICommand[]>, options?: IRetrieveRecordCommandOptions): Promise<ICommand[]> {
        //return empty ribbon for grouped records
        if (options.isGrouped) {
            return [];
        }
        const defaultCommands = (await defaultAction(options)).filter(cmd => !DataProvider.CONST.PLATFORM_COMMAND_IDS_SET.has(cmd.commandId));
        const { filterByPriority, useNestedFormat, specificCommands = undefined, recordIds = undefined } = options ?? {};
        if (filterByPriority || useNestedFormat) {
            console.warn('filterByPriority and useNestedFormat parameters are currently not supported in Portal.');
        }
        const commands = await (<PortalFetchXmlDataProvider>this.getTopLevelDataProvider()).ribbon.getCommands(specificCommands, recordIds);
        return [...defaultCommands, ...commands];
    }
    public setRibbon(ribbon: DatasetRibbon) {
        this.ribbon = ribbon;
    }
}

export class Grid extends Dataset<PortalFetchXmlDataProvider> {
    private _relationship: IXrmGridRelationship;
    private _datasetDependencies: DatasetDependencies;
    private _initialized: boolean = false;
    private _entityDefinition: IEntityDefinition;
    private _viewId: string;
    private _viewDefinition: IViewDefinition
    private _initialFetchXml: string;
    private _viewSelector: ViewSelector;
    private _ribbon: DatasetRibbon;
    private _quickFind: QuickFind;
    private _gridContext: GridContext;
    private _state: IState;
    private _stateRef: React.MutableRefObject<State>;
    private _pageId: string;
    private _reloadGridHandler: () => void = () => { };
    protected _dataProvider: PortalFetchXmlDataProvider = null;

    constructor(dependencies: DatasetDependencies, state: IState, stateRef: React.MutableRefObject<State>) {
        const provider = new PortalFetchXmlDataProvider({
            fetchXml: "",
            layoutCache: layoutCache,
            titleCache: titleCache,
            quickFindViewCache: quickFindCache
        });
        provider.setEntityName(dependencies.getEntityName());
        super(provider);
        this._viewDefinition = dependencies.getViewDefinition();
        this._viewId = dependencies.getViewDefinition().queryid;
        this._dataProvider = provider;
        this._datasetDependencies = dependencies;
        this._viewSelector = new ViewSelector(this);
        this._ribbon = new DatasetRibbon(this);
        this._quickFind = new QuickFind(this);
        this._gridContext = new GridContext(this, dependencies);
        this._state = state;
        this._stateRef = stateRef;
        this._pageId = this.formContext?.pageId ?? crypto.randomUUID();
        provider.setRibbon(this._ribbon);
    }

    public async init(): Promise<boolean> {
        if (this.isRelatedSubgridOnNewRecord) {
            return false;
        }
        const entityName = this.getTargetEntityType();
        let quickFindViewDefinition: IQuickFindView;
        [this._entityDefinition, quickFindViewDefinition] = await Promise.all([
            EntityDefinition.getAsync(entityName),
            ViewDefinition.getQuickFindViewAsync(entityName)
        ]);
        this._relationship = await this._getRelationship(entityName, this._entityDefinition);
        layoutCache.set(this._viewDefinition.queryid, this._layoutToColumns(JSON.parse(this._viewDefinition.layoutjson)));
        quickFindCache.set(entityName, quickFindViewDefinition.fetchxml);
        let baseFetchXml = this._stateRef.current.getValues<{ fetchXml: string }>('native').fetchXml;
        let title;
        [title, baseFetchXml] = await Promise.all([
            //the localized title should ideally be in the current view definition, it currently always gets czech translations
            (await ViewDefinition.getViewNamesAndIds(entityName, { includeUserQueries: this.userQueriesEnabled })).find(x => x.queryid === sanitizeGuid(this._viewId))?.name ?? this._viewDefinition.name,
            (async () => {
                //get our initialFetchXml which will always be the same and will not be affected by any state
                this._initialFetchXml = await this._getInitialFetchXml(entityName, this._entityDefinition, this._viewDefinition, await this._getQuickFindFilterExpression(entityName), this._relationship);
                //use either stored fetchXml from state or the initial one
                return baseFetchXml || this._initialFetchXml;
            })()
        ]);
        this.setDataSource(baseFetchXml);
        this.paging.setPageSize(this._getDefaultPageSize());
        this.setCurrencies(TransactionCurrencyDefinition.getCurrencies());
        titleCache.set(this._viewDefinition.queryid, title);
        //some (mostly native views) do not have savedqueryid in fetchXml, but they still have it in view definition, making it possible to retrieve layout
        this._dataProvider.getViewId = () => this._viewDefinition.queryid;
        if (!this.formContext) {
            //todo: tyopings
            HistoryManager.getCurrentPage().setDataset(this as any);
        }
        this._initialized = true;
        return true;
    }

    public get isInitialized() {
        return this._initialized;
    }

    public get isRelatedSubgridOnNewRecord() {
        if (this.formContext && !this.formContext.entityId && this.bindings.RelationshipName) {
            return true;
        }
        return false;
    }

    public get bindings() {
        return this._datasetDependencies.getControlBindings();
    }

    public get datasetBindingName() {
        return this._datasetDependencies.getDatasetBindingName();
    }

    public get isHomePageGrid() {
        return this.gridType === XrmGridType.HomepageGrid;
    }

    public get relationship() {
        return this._relationship;
    }

    public get gridType() {
        if (this.bindings.IsViewPage) {
            return XrmGridType.HomepageGrid;
        }
        return XrmGridType.Subgrid;
    }

    public get initialFetchXml() {
        return this._initialFetchXml;
    }

    public get cdsOptions() {
        return this._datasetDependencies.getCdsOptions();
    }

    public getCustomControlDescriptions() {
        return this._datasetDependencies.getCustomControlDescriptions();
    }

    public destroy(): void {
        //needed for query builder to persist state
        if (this._initialFetchXml !== this.getDataProvider().getDataSource()) {
            this.getStateRef().current.setValues('native', {
                fetchXml: this.getDataProvider().getDataSource()
            });
        }
        super.destroy();
    }

    public get formContext() {
        return this._datasetDependencies.getFormContext();
    }

    public get ribbon() {
        return this._ribbon;
    }

    public get quickFind() {
        return this._quickFind;
    }

    public get gridContext() {
        return this._gridContext;
    }

    public get userQueriesEnabled() {
        return this._datasetDependencies.areUserQueriesEnabled();
    }

    public get viewSelector() {
        return this._viewSelector;
    }

    public get quickFindColumns() {
        return this.getDataProvider().getQuickFindColumns();
    }
    public setReloadGridHandler(reloadGridHandler: () => void) {
        this._reloadGridHandler = reloadGridHandler;
    }

    public reloadGrid() {
        this._reloadGridHandler();
    }

    public getPageId(): string {
        return this._pageId;
    }

    public getState() {
        return this._state;
    }

    public getStateRef() {
        return this._stateRef;
    }

    private _getDefaultPageSize() {
        if (this.bindings.RecordsPerPage) {
            return parseInt(this.bindings.RecordsPerPage.value);
        }
        if (this.gridType === XrmGridType.HomepageGrid) {
            return Constants.DEFAULT_PAGE_SIZE_GRID;
        }
        return Constants.DEFAULT_PAGE_SIZE_SUBGRID;
    }

    private async _getQuickFindFilterExpression(entityName: string): Promise<ComponentFramework.PropertyHelper.DataSetApi.FilterExpression> {
        const quickFindViewDefinition = await ViewDefinition.getQuickFindViewAsync(entityName);
        const parsedDefinition = DomParser.parseFromString(quickFindViewDefinition.fetchxml, "text/xml");
        const conditions = await Promise.all([...parsedDefinition.getElementsByTagName("condition")].filter(x => x.getAttribute("value") === "{0}").map(async x => {
            let attributeName = x.getAttribute('attribute');
            const linkedEntityAlias = x.getAttribute('entityname');
            const attributeMetadata = await new FetchXml(quickFindViewDefinition.fetchxml).attribute(linkedEntityAlias ? `${linkedEntityAlias}.${attributeName}` : attributeName).getMetadata();

            switch (Attribute.GetDataTypeFromMetadata(attributeMetadata)) {
                case DataTypes.LookupSimple:
                case DataTypes.LookupOwner:
                case DataTypes.LookupCustomer: {
                    attributeName += "name";
                }
            }
            return {
                attributeName: attributeName,
                conditionOperator: Operators.Like.Value,
                value: "",
                entityAliasName: x.getAttribute('entityname')
            };
        }));

        return {
            filterOperator: 1,
            conditions: conditions
        };
    }

    private _getRelationship(entityName: string, entityDefinition: IEntityDefinition): IXrmGridRelationship | null {
        if (!this.bindings.RelationshipName?.value) {
            return null;
        }
        let relationship = entityDefinition.ManyToOneRelationships.find(x => x.SchemaName === this.bindings.RelationshipName.value);
        if (relationship) {
            return {
                attributeName: relationship.ReferencingAttribute,
                entityName: entityName,
                name: relationship.SchemaName,
                recordId: this.formContext.entityId,
                relationshipType: XrmRelationshipType.OneToMany,
                metadata: {
                    oneToMany: relationship
                }
            };
        }
        else if (entityDefinition.ManyToManyRelationships.find(x => x.SchemaName === this.bindings.RelationshipName.value)) {
            const many2Many = entityDefinition.ManyToManyRelationships.find(x => x.SchemaName === this.bindings.RelationshipName.value);
            // Correctly locate the relationship direction - either parent > entity1 > child or parent > entity2 > child
            const currentEntityIntersectAttribute = many2Many.Entity1LogicalName === entityName ? many2Many.Entity1IntersectAttribute : many2Many.Entity2IntersectAttribute;
            const relatedEntityIntersectAttribute = many2Many.Entity1LogicalName === entityName ? many2Many.Entity2IntersectAttribute : many2Many.Entity1IntersectAttribute;

            return {
                attributeName: many2Many.Entity2IntersectAttribute,
                entityName: entityName,
                name: many2Many.SchemaName,
                recordId: this.formContext.entityId,
                relationshipType: XrmRelationshipType.ManyToMany,
                metadata: {
                    many2Many: {
                        ...many2Many,
                        CurrentEntityIntersectAttribute: currentEntityIntersectAttribute,
                        RelatedEntityIntersectAttribute: relatedEntityIntersectAttribute
                    }
                }
            };
        }
        throw new Error(`Unable to find relationship ${this.bindings.RelationshipName.value} in entity definitions!`);
    }

    //needs to be run after relationship and quickfind filters get resolved
    private async _getInitialFetchXml(entityName: string, entityDefinition: IEntityDefinition, viewDefinition: IViewDefinition, quickFindFilterExpression: ComponentFramework.PropertyHelper.DataSetApi.FilterExpression, relationship?: IXrmGridRelationship): Promise<string> {
        const fetchXml = new FetchXml(viewDefinition.fetchxml).builder;
        // Distinct aliases https://codeburst.io/javascript-array-distinct-5edc93501dc4
        const aliases = [...new Set(quickFindFilterExpression.conditions.map(x => x.entityAliasName).filter(x => x !== null && x !== undefined))];
        const quickFindViewDefinition = await ViewDefinition.getQuickFindViewAsync(entityName);
        const parsedDefinition = DomParser.parseFromString(quickFindViewDefinition.fetchxml, "text/xml");
        const linkedEntities = [...parsedDefinition.getElementsByTagName("link-entity")];
        aliases.map(alias => {
            const link = linkedEntities.find(x => x.getAttribute("alias") === alias);
            fetchXml.entity.addLinkEntity(new FetchXmlBuilder.linkEntity(
                link.getAttribute('name'),
                link.getAttribute('from'),
                link.getAttribute('to'),
                link.getAttribute('link-type'),
                alias
            ));
        });
        // If FetchXML doesn't contain the PrimaryId, it needs to be added so that we can create links etc.
        fetchXml.entity.attributes = [...fetchXml.entity.attributes.filter(attr => attr.name !== entityDefinition.PrimaryIdAttribute), new FetchXmlBuilder.attribute(entityDefinition.PrimaryIdAttribute)];
        if (!relationship) {
            return fetchXml.toXml();
        }
        if (relationship.relationshipType === XrmRelationshipType.OneToMany) {
            const oneToMany = relationship.metadata.oneToMany;
            fetchXml.entity.addLinkEntity(new FetchXmlBuilder.linkEntity(
                oneToMany.ReferencedEntity,
                oneToMany.ReferencedAttribute,
                oneToMany.ReferencingAttribute,
                "inner",
                "__relatedEntityFilter"
            ));
            fetchXml.entity.linkEntities.find(x => x.alias === '__relatedEntityFilter').addFilter(
                new FetchXmlBuilder.filter(
                    Type.GetNameFromValue(0),
                    [new FetchXmlBuilder.condition(
                        oneToMany.ReferencedAttribute,
                        Operators.GetNameFromValue(0),
                        [new FetchXmlBuilder.value(this.formContext.entityId)]
                    )]
                )
            );
        }
        else {
            const many2Many = this.relationship.metadata.many2Many;
            const isSelfReferenced = many2Many.Entity1LogicalName === many2Many.Entity2LogicalName;
            const intersectTableLinkedEntity = new FetchXmlBuilder.linkEntity(
                many2Many.IntersectEntityName,
                many2Many.CurrentEntityIntersectAttribute,
                many2Many.CurrentEntityIntersectAttribute,
                "inner",
                '__relatedEntityFilter',
            );
            const linkedEntity = new FetchXmlBuilder.linkEntity(
                entityName === many2Many.Entity2LogicalName ? many2Many.Entity1LogicalName : many2Many.Entity2LogicalName,
                isSelfReferenced ? entityDefinition.PrimaryIdAttribute : entityName === many2Many.Entity2LogicalName ? many2Many.Entity1IntersectAttribute : many2Many.Entity2IntersectAttribute,
                entityName === many2Many.Entity2LogicalName ? many2Many.Entity1IntersectAttribute : many2Many.Entity2IntersectAttribute,
                "inner"
            );
            linkedEntity.addFilter(
                new FetchXmlBuilder.filter(
                    Type.GetNameFromValue(0),
                    [new FetchXmlBuilder.condition(
                        many2Many.RelatedEntityIntersectAttribute,
                        Operators.GetNameFromValue(0),
                        [new FetchXmlBuilder.value(this.formContext.entityId)]
                    )]
                )
            );
            intersectTableLinkedEntity.addLinkEntity(linkedEntity);
            fetchXml.entity.addLinkEntity(intersectTableLinkedEntity);
        }
        return fetchXml.toXml();
    }

    private _layoutToColumns(layout: ILayout): IColumn[] {
        return layout.Rows[0].Cells.map(x => {
            return {
                name: x.Name,
                isHidden: x.IsHidden,
                visualSizeFactor: x.Width,
                ...x.Column
            };
        });
    }
}